'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  describeDifference,
  formatOffset,
  offsetMinutes,
  zoneCity,
  zoneName,
} from '@/lib/place-time';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

function timeIn(date: Date, timeZone: string, hour12: boolean, seconds = true) {
  return date.toLocaleTimeString('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hour12,
  });
}

function dateIn(date: Date, timeZone: string) {
  return date.toLocaleDateString('en-US', {
    timeZone,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function BrowserPlaceTime({
  config,
}: {
  config?: { toolSlug?: string; heading?: string; subheading?: string; variant?: string };
}) {
  const zones = useMemo(
    () => (config?.variant ?? 'Europe/London').split(',').filter(Boolean),
    [config?.variant],
  );
  const [now, setNow] = useState<Date | null>(null);
  const [hour12, setHour12] = useState(true);
  const [myTime, setMyTime] = useState('09:00');
  const [localZone, setLocalZone] = useState('UTC');
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'time-in-london', '').catch(() => undefined);
  };

  useEffect(() => {
    setNow(new Date());
    setLocalZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const main = zones[0];
  const converted = useMemo(() => {
    if (!now) return null;
    const [h, m] = myTime.split(':').map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    // Today at h:m on the viewer's clock, then shown in each zone.
    const base = new Date(now);
    base.setHours(h, m, 0, 0);
    return zones.map((tz) => ({
      tz,
      text: `${timeIn(base, tz, hour12, false)}, ${base.toLocaleDateString('en-US', { timeZone: tz, weekday: 'short' })}`,
    }));
  }, [now, myTime, zones, hour12]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Local time</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Local time'}</h2>
        <p className="tool-live-subtitle">
          Live from your device clock and the time-zone database. Daylight saving is applied
          automatically.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-preview">
          {now ? (
            <>
              <p
                aria-live="off"
                style={{
                  fontSize: 'clamp(2.5rem, 9vw, 4.5rem)',
                  fontWeight: 700,
                  lineHeight: 1.05,
                  fontVariantNumeric: 'tabular-nums',
                  margin: 0,
                }}
              >
                {timeIn(now, main, hour12)}
              </p>
              <p className="tool-live-subtitle" style={{ margin: '0.25rem 0 0' }}>
                {dateIn(now, main)}
              </p>
              <p className="tool-live-panel-note">
                {zoneName(now, main)} ({formatOffset(offsetMinutes(now, main))})
                {localZone !== main
                  ? `, ${describeDifference(offsetMinutes(now, main) - offsetMinutes(now, localZone))} your time`
                  : ', the same as your time zone'}
              </p>
              {zones.length > 1 ? (
                <div style={{ display: 'grid', gap: '0.4rem', marginTop: '0.75rem' }}>
                  {zones.map((tz) => (
                    <div
                      key={tz}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '1rem',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      <span>
                        {zoneName(now, tz, 'longGeneric')} ({zoneCity(tz)})
                      </span>
                      <strong>{timeIn(now, tz, hour12, false)}</strong>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="tool-live-panel-note">Loading the local time…</p>
          )}
        </section>
        <section className="tool-live-uploader">
          <label className="tool-live-field">
            <span>Clock format</span>
            <select
              onChange={(e) => {
                report();
                setHour12(e.target.value === '12');
              }}
              value={hour12 ? '12' : '24'}
            >
              <option value="12">12-hour</option>
              <option value="24">24-hour</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>Your time ({localZone.replace(/_/g, ' ')})</span>
            <input
              onChange={(e) => {
                report();
                setMyTime(e.target.value);
              }}
              type="time"
              value={myTime}
            />
          </label>
          {converted ? (
            <div className="tool-live-panel-note" style={{ display: 'grid', gap: '0.25rem' }}>
              {converted.map((c) => (
                <span key={c.tz}>
                  {zones.length === 1 && config?.subheading ? config.subheading : zoneCity(c.tz)}:{' '}
                  <strong>{c.text}</strong>
                </span>
              ))}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
